import React, { useState, useEffect, useRef } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import useStyles from 'isomorphic-style-loader/useStyles';
import s from './Stats.css';
import useScrollReveal from '../../hooks/useScrollReveal';
import messages from '../../../../locale/messages';

const items = [
  { id: 'ordersDelivered', label: messages.homeStatsOrdersDelivered, value: '30,000+', numeric: 30000, suffix: ',000+' },
  { id: 'activeMerchants', label: messages.homeStatsActiveMerchants, value: '600+', numeric: 600, suffix: '+' },
  { id: 'happyCustomers', label: messages.homeStatsHappyCustomers, value: '10,000+', numeric: 10000, suffix: ',000+' },
  { id: 'deliveryTime', label: messages.homeStatsDeliveryTime, valueMessage: messages.homeStatsDeliveryTimeValue, numeric: null },
];

const DURATION = 1500; // ms

function useCountUp(target, active) {
  const [count, setCount] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!active || target === null) return undefined;

    const start = performance.now();

    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / DURATION, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [active, target]);

  return count;
}

function StatItem({ item, active, delay, intl }) {
  const count = useCountUp(item.numeric, active);

  const display = () => {
    if (item.numeric === null) return intl.formatMessage(item.valueMessage);
    if (!active) return item.value;
    if (count >= item.numeric) return item.value;

    if (item.numeric >= 1000) {
      const thousands = Math.floor(count / 1000);
      return `${thousands},${String(count % 1000).padStart(3, '0')}+`;
    }
    return `${count}+`;
  };

  return (
    <div
      className={`${s.item} ${active ? s.itemVisible : ''}`}
      style={{ transitionDelay: active ? `${delay}s` : '0s' }}
    >
      <p className={s.label}>
        <FormattedMessage {...item.label} />
      </p>
      <p className={s.value}>{display()}</p>
    </div>
  );
}

function Stats({ intl }) {
  useStyles(s);
  const { ref, isVisible } = useScrollReveal(0.3);

  return (
    <section className={s.stats} ref={ref}>
      <div className={`${s.inner} ${isVisible ? s.innerVisible : ''}`}>
        {items.map((item, i) => (
          <StatItem
            key={item.id}
            item={item}
            active={isVisible}
            delay={0.15 + i * 0.1}
            intl={intl}
          />
        ))}
      </div>
    </section>
  );
}

export default injectIntl(Stats);
